/**
 * PlatformAssistant
 * -----------------
 * A Neo4j-Aura-style "how do I use this product" chat dock.
 *
 * Hard rules (matches the BE):
 *   - Product help uses only the current workspace's selected model key.
 *     Without one, the deterministic docs remain available.
 *   - Visually distinct from agents UI (different gradient, "Platform
 *     assistant" pill, "verify all output" footer) so users never confuse
 *     it with their own grounded agents.
 *   - Bottom-right floating bubble. Click to expand into a 380×520 panel.
 *   - Authenticated routes only — mounted from Layout when `token` is set.
 */
import { useEffect, useRef, useState } from "react";
import { Bot, MessageCircle, Send, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { rawFetch } from "@/lib/api/client";

type Msg = { role: "user" | "assistant"; content: string };

const SEED: Msg[] = [
  {
    role: "assistant",
    content:
      "Hi — I'm the Ontoligent platform assistant. I can walk you through BYOK setup, generating an ontology from a domain description, adopting an external standard through its permitted reference, alignment, or import mode, exporting to JSON-LD / Turtle / OWL / Cypher / CSV, opening the ontology card, spinning up an industry agent template, governance & evals, and packaging an Orb. External source content is never assumed bundled. What would you like to do?",
  },
];

export function PlatformAssistant() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(SEED);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, msgs]);

  async function send() {
    const text = draft.trim();
    if (!text || busy) return;
    const next: Msg[] = [...msgs, { role: "user", content: text }];
    setMsgs(next);
    setDraft("");
    setBusy(true);
    try {
      const r = await rawFetch<{ reply: string; fallback?: boolean }>(
        "/iam/assistant/",
        { method: "POST", body: JSON.stringify({ messages: next }) },
      );
      setMsgs((prev) => [...prev, { role: "assistant", content: r.reply }]);
    } catch {
      setMsgs((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "The assistant is temporarily unreachable. Try Studio → How-to or Studio → ORB guide for step-by-step instructions.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-accent/40 bg-panel text-accent shadow-xl hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label="Open platform assistant"
        title="Platform assistant"
      >
        <MessageCircle className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Ontoligent platform assistant"
      className="fixed bottom-3 right-3 z-40 flex h-[min(520px,calc(100dvh-1.5rem))] w-[min(380px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-xl border border-accent/40 bg-panel shadow-2xl sm:bottom-5 sm:right-5"
      style={{
        background:
          "linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--bg)/0.92) 100%)",
      }}
    >
      <header className="flex items-center justify-between border-b border-border bg-gradient-to-r from-accent/15 via-transparent to-transparent px-3 py-2">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold">Platform assistant</span>
          <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-accent">
            product help
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close assistant"
          className="rounded p-1 text-muted hover:bg-bg hover:text-text"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div
        ref={scrollRef}
        className="scroll-thin flex-1 space-y-2 overflow-auto px-3 py-3 text-sm"
      >
        {msgs.map((m, i) => (
          <div
            key={i}
            className={
              m.role === "user"
                ? "ml-auto max-w-[85%] rounded-lg bg-accent/15 px-3 py-2 text-text"
                : "mr-auto max-w-[90%] rounded-lg bg-bg/60 px-3 py-2 text-text border border-border"
            }
          >
            {m.role === "assistant" ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ children, href }) => href?.startsWith("/") && !href.startsWith("//")
                    ? <a href={href} className="text-accent underline underline-offset-2">{children}</a>
                    : <span>{children}</span>,
                  img: () => null,
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>,
                  code: ({ children }) => <code className="rounded bg-panel px-1 py-0.5 font-mono text-[12px] text-accent">{children}</code>,
                  pre: ({ children }) => <pre className="mb-2 overflow-x-auto rounded border border-border bg-panel p-2 text-[12px] last:mb-0">{children}</pre>,
                  strong: ({ children }) => <strong className="font-semibold text-text">{children}</strong>,
                }}
              >
                {m.content}
              </ReactMarkdown>
            ) : (
              <p className="whitespace-pre-wrap break-words text-[13px] leading-snug">{m.content}</p>
            )}
          </div>
        ))}
        {busy && (
          <div className="mr-auto max-w-[90%] rounded-lg border border-border bg-bg/60 px-3 py-2 text-xs text-muted">
            Thinking…
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-border p-2"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Ask: how do I add my OpenAI key? how do I publish an Orb?"
            className="flex-1 resize-none rounded border border-border bg-bg p-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="rounded bg-accent px-2 py-2 text-bg disabled:opacity-50"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-[10px] text-muted">
          Product help uses your workspace key. It cannot read documents or run tools.
        </p>
      </form>
    </div>
  );
}
